import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CaretLeft, Lock, CheckCircle, Warning, Clock } from '@phosphor-icons/react';
import { PillInput, PillButton } from '../components/PillKit';
import { authExtrasAPI, formatApiError } from '../services/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

export default function ForcedPasswordResetPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = location.state?.userId || '';
  const resetTicket = location.state?.resetTicket || '';

  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('form'); // form | success | expired | used | invalid

  const pwdOk = newPwd.length >= 8 && /[A-Za-z]/.test(newPwd) && /[0-9]/.test(newPwd);
  const match = newPwd && newPwd === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwdOk || !match) return;
    setLoading(true);
    try {
      await authExtrasAPI.completeForcedReset(userId, resetTicket, newPwd);
      setStatus('success');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const msg = formatApiError(err) || '';
      if (msg.toLowerCase().includes('expired')) setStatus('expired');
      else if (msg.toLowerCase().includes('already used') || msg.toLowerCase().includes('invalid reset ticket')) setStatus('used');
      else setStatus('invalid');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="forced-reset-page">
      <header className="px-5 pt-6 pb-2 flex items-center justify-between">
        <Link to="/login" className="text-white p-1" aria-label="Back" data-testid="forced-reset-back-btn">
          <CaretLeft className="w-6 h-6" />
        </Link>
        <Link to="/"><img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" /></Link>
        <span className="w-6" />
      </header>

      <main className="flex-1 max-w-md w-full mx-auto px-6 pt-8 pb-12">
        {!userId || !resetTicket ? (
          <div className="text-center" data-testid="forced-reset-invalid-panel">
            <div className="w-20 h-20 mx-auto rounded-full bg-red-500/15 border-[3px] border-red-500 flex items-center justify-center mb-6">
              <Warning className="w-10 h-10 text-red-500" weight="fill" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Reset session missing
            </h1>
            <p className="text-white/60 text-sm mb-8">Please log in again to start a new reset.</p>
            <Link to="/login"><PillButton variant="solid" type="button" data-testid="forced-reset-login-btn">Back to sign in</PillButton></Link>
          </div>
        ) : status === 'success' ? (
          <div className="text-center" data-testid="forced-reset-done-panel">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#22C55E]/15 border-[3px] border-[#22C55E] flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-[#22C55E]" weight="fill" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Password updated
            </h1>
            <p className="text-white/60 text-sm mb-8">Redirecting you to sign in…</p>
            <Link to="/login"><PillButton variant="solid" type="button" data-testid="forced-reset-signin-btn">Sign in now</PillButton></Link>
          </div>
        ) : status === 'expired' ? (
          <div className="text-center" data-testid="forced-reset-expired-panel">
            <div className="w-20 h-20 mx-auto rounded-full bg-orange-500/15 border-[3px] border-orange-500 flex items-center justify-center mb-6">
              <Clock className="w-10 h-10 text-orange-500" weight="fill" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Reset link expired
            </h1>
            <p className="text-white/60 text-sm mb-8">Log in again to get a new reset ticket.</p>
            <Link to="/login"><PillButton variant="solid" type="button" data-testid="forced-reset-login-btn">Back to sign in</PillButton></Link>
          </div>
        ) : status === 'used' ? (
          <div className="text-center" data-testid="forced-reset-used-panel">
            <div className="w-20 h-20 mx-auto rounded-full bg-orange-500/15 border-[3px] border-orange-500 flex items-center justify-center mb-6">
              <Warning className="w-10 h-10 text-orange-500" weight="fill" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Already used
            </h1>
            <p className="text-white/60 text-sm mb-8">This reset ticket was already used. Log in again for a new one.</p>
            <Link to="/login"><PillButton variant="solid" type="button" data-testid="forced-reset-login-btn">Back to sign in</PillButton></Link>
          </div>
        ) : (
          <>
            <div className="mb-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-white/5 border-[3px] border-white flex items-center justify-center mb-5">
                <Lock className="w-8 h-8 text-[#22C55E]" weight="duotone" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold leading-none tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Set a new password
              </h1>
              <p className="text-[#22C55E] text-base sm:text-lg font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
                Required before you continue
              </p>
            </div>

            {status === 'invalid' && (
              <p className="text-red-400 text-sm text-center mb-4" data-testid="forced-reset-error-msg">
                Something went wrong. Please try again or log in again.
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <PillInput
                id="forced-new-pwd"
                type="password"
                required
                autoFocus
                minLength={8}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="New password"
                data-testid="forced-reset-new-password-input"
              />
              <PillInput
                id="forced-confirm-pwd"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm password"
                data-testid="forced-reset-confirm-password-input"
              />

              <div className="space-y-1.5 px-2 pt-1">
                <p className={`flex items-center gap-2 text-xs ${pwdOk ? 'text-[#22C55E]' : 'text-white/40'}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pwdOk ? '#22C55E' : '#444' }} />
                  8+ characters with letters &amp; numbers
                </p>
                <p className={`flex items-center gap-2 text-xs ${match ? 'text-[#22C55E]' : 'text-white/40'}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: match ? '#22C55E' : '#444' }} />
                  Passwords match
                </p>
              </div>

              <PillButton
                type="submit"
                variant="solid"
                disabled={loading || !pwdOk || !match}
                data-testid="forced-reset-submit-btn"
              >
                {loading ? 'Saving…' : 'Update password'}
              </PillButton>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
